import React, { createContext, PropsWithChildren, useEffect, useState } from 'react';
import {
    Analytics,
    initializeAnalytics,
    logEvent,
    setUserProperties,
} from 'firebase/analytics';
import { FirebaseApp, initializeApp } from 'firebase/app';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import Logger from '@/utils/logging';

const firebaseConfig = {
    apiKey: 'AIzaSyAWMv99uaZw6FnGtKFZ2LR8VlvlAopXx6w',
    authDomain: 'journey-based-listing.firebaseapp.com',
    projectId: 'journey-based-listing',
    storageBucket: 'journey-based-listing.appspot.com',
    messagingSenderId: '32814436608',
    appId: '1:32814436608:web:b45df054052ac9dcd5a3e5',
    measurementId: 'G-EP9K2MCB2Y',
};

export interface AnalyticsProps {
    firebaseApp: FirebaseApp | null;
    firebaseAnalytics: Analytics | null;
    firebaseLogEvent: (name: string, data: Record<string, string>) => void;
}

export const AnalyticsContext = createContext<AnalyticsProps>({
    firebaseApp: null,
    firebaseAnalytics: null,
    firebaseLogEvent: () => null,
});

export const AnalyticsContextProvider = ({
    location,
    children,
}: PropsWithChildren<{ location: Location }>) => {
    const [firebaseApp, setFirebaseApp] = useState<FirebaseApp | null>(null);
    const [firebaseAnalytics, setFirebaseAnalytics] = useState<Analytics | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const firebaseLogEvent = (name: string, data: Record<string, string>) => {
        if (!firebaseAnalytics || !firebaseApp) {
            if (!(window.rapidstor_settings.rapidstor_options.configSelector)) {
                return;
            }
            Logger.info('Firebase analytics not initialized', location);
            return;
        }

        logEvent(firebaseAnalytics, name, data);
    };

    const setupFirebaseInstance = (location: Location, analytics: Analytics) => {
        setUserProperties(analytics, {
            location_code: location.locationCode,
            corporation_code: location.corpCode,
            location_name: location.name,
        });

        Logger.info('Firebase analytics setup complete');
    };

    useEffect(() => {
        if (window.rapidstor_settings.rapidstor_options.configSelector) {
            Logger.info(
                'Config selector or dev mode is enabled, skipping Firebase initialization'
            );
            return;
        }

        const initializeFirebase = async () => {
            try {
                const app = initializeApp(firebaseConfig);
                setFirebaseApp(app);
                const analytics = initializeAnalytics(app, {
                    config: {
                        send_page_view: true,
                    },
                });
                setFirebaseAnalytics(analytics);
                setIsInitialized(true);
                Logger.info('Firebase initialized successfully');
            } catch (error) {
                Logger.error('Error initializing Firebase');
            }
        };

        if (!isInitialized) {
            initializeFirebase();
        }
    }, [isInitialized]);

    useEffect(() => {
        if (isInitialized && firebaseApp && firebaseAnalytics && location) {
            setupFirebaseInstance(location, firebaseAnalytics);
        }
    }, [isInitialized, firebaseApp, firebaseAnalytics, location]);

    return (
        <AnalyticsContext.Provider
            value={{
                firebaseApp,
                firebaseAnalytics,
                firebaseLogEvent,
            }}
        >
            {children}
        </AnalyticsContext.Provider>
    );
};
